/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useState } from 'react'

import { CryptoGuideDialog, LoadingDialog } from '..'
import styled from 'styled-components'

import { useModalContext } from '../../../contexts/ModalContext'
import { useModalGuideContext } from '../../../contexts/ModalGuideContext'
import { PrimaryButton } from '../../Button'
import Card from '../../Card'
import FlexBox from '../../Common/FlexBox'

export type Props = {
  hostedUrl?: string
}

const DonateCrypto = ({ hostedUrl }: Props) => {
  const { setModalConfig } = useModalContext()
  const { openModal: openModalGuide } = useModalGuideContext()

  const [isLoadingFrame, setIsLoadingFrame] = useState<boolean>(true)

  const onFrameLoad = useCallback(() => {
    setIsLoadingFrame(false)
    setModalConfig((prev) => ({
      ...prev,
      asWidgetContainer: true,
      showCloseButton: true
    }))
  }, [setModalConfig])

  if (!hostedUrl) {
    return null
  }

  return (
    <StyledContainer flexDirection="column" rowGap={!isLoadingFrame ? 12 : 0} justifyContent="center" alignItems="center">
      {!isLoadingFrame && (
        <StyledGuideCard>
          <FlexBox justifyContent="space-between" alignItems="center">
            <span>Need help?</span>
            <PrimaryButton
              size="small"
              outlined
              onClick={() =>
                openModalGuide(<CryptoGuideDialog />, {
                  fullscreen: true,
                  showCloseButton: true,
                  showDialogFooter: false,
                  showDialogHeader: false
                })
              }
            >
              Open the Crypto Guide
            </PrimaryButton>
          </FlexBox>
        </StyledGuideCard>
      )}

      {isLoadingFrame && <LoadingDialog />}
      <StyledHostedTripleAWindow $isLoading={isLoadingFrame} src={hostedUrl} onLoad={onFrameLoad} />
    </StyledContainer>
  )
}

export default DonateCrypto

const StyledContainer = styled(FlexBox)`
  height: 100%;
`

const StyledGuideCard = styled(Card)`
  width: 350px;
  margin-inline: auto;
  padding: 16px 30px;

  span {
    font-size: ${({ theme }) => theme.units.fontSize.regular};
  }

  @media screen and (min-width: 1921px) {
    button {
      height: 40px;
      font-size: ${({ theme }) => theme.units.fontSize.regular};
    }
  }
`

const StyledHostedTripleAWindow = styled.iframe<{
  $isLoading: boolean
}>`
  flex-grow: 1;
  width: 350px;
  height: ${({ $isLoading }) => ($isLoading ? 0 : 'auto')};
  margin-inline: auto;
  border: none;
  visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : undefined)};
`
